/**
 * AROX VOD landing page behaviour (loaded by snippets/arox-head.liquid).
 *
 * Progressive enhancement only — every CTA is a real link and the order form is a
 * native Shopify product form, so the page works without this module.
 * - Offer selection stays in sync across the order form, total, call to action, offer cards and sticky bar.
 * - The order form leaves exactly one unit of the selected AROX variant in the cart (removing
 *   the other AROX offer, keeping unrelated items) via the Ajax Cart API, then opens Shopify
 *   checkout. On network failure it falls back to the native form post.
 * - One-page COD mode (order section setting): the same form collects four fields (first name, last
 *   name, phone, full address) and posts them to the AROX order app, which creates the unpaid order
 *   in Shopify. Errors stay on the page; only a load without JavaScript uses the native checkout form.
 * - Analytics: publishes custom events through Shopify's customer events API when available:
 *   `arox_offer_selected`, `arox_order_started` (first interaction with the one-page form) and
 *   `arox_order_placed` (only after the app confirmed a created Shopify order; carries the
 *   event_id shared with the server-side event). No name, phone or address is ever published.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const isRtl = () => document.documentElement.dir === 'rtl';
const root = window.Shopify?.routes?.root ?? '/';

/** Height of the sticky site header, so scroll targets land below it. */
function headerOffset() {
  const header = document.querySelector('.arox-section-header');
  const height = header instanceof HTMLElement ? Math.round(header.getBoundingClientRect().height) : 0;
  document.documentElement.style.setProperty('--arox-header-offset', `${height}px`);
  return height;
}

/**
 * Scrolls so a purchase block starts just below the sticky header. If the block is taller than
 * the viewport, its call to action wins: the scroll goes further until `bottom` is in view.
 * @param {number} top - Viewport-relative top of the block
 * @param {number} bottom - Viewport-relative bottom of the call(s) to action
 * @param {ScrollBehavior} behavior
 */
function revealBlock(top, bottom, behavior) {
  const gap = 12;
  let y = top + window.scrollY - headerOffset() - gap;
  const overflow = bottom + window.scrollY + gap - (y + window.innerHeight);
  if (overflow > 0) y += overflow;
  y = Math.max(0, Math.round(y));
  // Long jumps start half a screen short and glide the rest: immediate, but still oriented.
  const distance = y - window.scrollY;
  if (behavior === 'smooth' && Math.abs(distance) > window.innerHeight) {
    window.scrollTo({ top: y - Math.sign(distance) * Math.round(window.innerHeight / 2), behavior: 'instant' });
  }
  window.scrollTo({ top: y, behavior });
}

const scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

/**
 * Scrolls to the order panel (#arox-buy) so the offer choices, the summary and the submit
 * button are all on screen below the sticky header (the submit button wins on short screens).
 * @param {ScrollBehavior} [behavior]
 * @returns {boolean} whether an order panel exists on this page
 */
function scrollToOrder(behavior = scrollBehavior()) {
  const anchor = document.querySelector('[data-arox-order-anchor]');
  if (!(anchor instanceof HTMLElement)) return false;
  const direct = anchor.parentElement?.querySelector('form[data-arox-direct-active]');
  const submit = direct ? null : anchor.parentElement?.querySelector('[data-arox-submit]');
  const top = anchor.getBoundingClientRect().top;
  revealBlock(top, submit instanceof HTMLElement ? submit.getBoundingClientRect().bottom : top, behavior);
  return true;
}

/**
 * Scrolls to the offers (#arox-plans), skipping the section heading. On phones that is the
 * compact plan picker (both plans and its purchase button); elsewhere the cards of the first row
 * (both side by side), including the ribbon above the featured card and each card's purchase
 * button. Either lands below the sticky header.
 * @param {ScrollBehavior} [behavior]
 * @returns {boolean} whether offers exist on this page
 */
function scrollToOffers(behavior = scrollBehavior()) {
  const anchor = document.querySelector('[data-arox-offers-anchor]');
  const container = anchor?.parentElement;
  if (!container) return false;
  const picker = container.querySelector('[data-arox-plan-picker]');
  if (picker instanceof HTMLElement && picker.getClientRects().length) {
    const cta = picker.querySelector('button[type="submit"]') ?? picker;
    revealBlock(picker.getBoundingClientRect().top, cta.getBoundingClientRect().bottom, behavior);
    return true;
  }
  const grid = container.querySelector('.arox-offers__grid');
  const cards = grid ? [...grid.children] : [];
  if (!cards.length) return false;
  const rowTop = cards[0].getBoundingClientRect().top;
  const row = cards.filter((card) => Math.abs(card.getBoundingClientRect().top - rowTop) < 2);
  // The featured card's ribbon overhangs the card's top edge.
  const tops = row.flatMap((card) => [card, ...card.querySelectorAll('.arox-offer__ribbon')]).map((el) => el.getBoundingClientRect().top);
  const bottoms = row.map((card) => (card.lastElementChild ?? card).getBoundingClientRect().bottom);
  revealBlock(Math.min(...tops), Math.max(...bottoms), behavior);
  return true;
}

const isOrderTarget = (el) => el.matches('[data-arox-order-anchor], #arox-order');
const isOffersTarget = (el) => el.matches('[data-arox-offers-anchor], #arox-offers');

/** @param {Element | null} target */
function scrollToTarget(target) {
  if (!target) return;
  if (isOrderTarget(target) && scrollToOrder()) return;
  if (isOffersTarget(target) && scrollToOffers()) return;
  target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
}

/** @returns {HTMLInputElement[]} */
function variantInputs() {
  return [...document.querySelectorAll('input[data-arox-variant]')].filter((el) => el instanceof HTMLInputElement);
}

/**
 * Reflects the checked variant everywhere it is displayed.
 * @param {HTMLInputElement} input
 * @param {boolean} [userInitiated]
 */
function syncSelection(input, userInitiated = false) {
  const { name = '', price = '' } = input.dataset;
  for (const el of document.querySelectorAll('[data-arox-summary-name], [data-arox-sticky-name]')) el.textContent = name;
  for (const el of document.querySelectorAll('[data-arox-summary-price], [data-arox-sticky-price]')) el.textContent = price;
  for (const el of document.querySelectorAll('[data-arox-price-label]')) {
    if (!el.closest('[aria-busy="true"]')) el.textContent = (el.getAttribute('data-arox-price-label') ?? '').replace('%PRICE%', price);
  }
  for (const card of document.querySelectorAll('[data-arox-offer-card]')) {
    card.classList.toggle('is-selected', card.getAttribute('data-arox-offer-card') === input.value);
  }

  if (!userInitiated) return;
  const url = new URL(window.location.href);
  url.searchParams.set('variant', input.value);
  window.history.replaceState(window.history.state, '', url);
  window.Shopify?.analytics?.publish?.('arox_offer_selected', { variantId: input.value, offer: name, price });
}

/** @param {MouseEvent} event */
function handleClick(event) {
  const link = event.target instanceof Element ? event.target.closest('a[data-arox-scroll]') : null;
  if (!(link instanceof HTMLAnchorElement)) return;

  const hash = new URL(link.href, window.location.href).hash;
  const target = hash ? document.getElementById(hash.slice(1)) : null;
  if (!target) return;
  event.preventDefault();

  const variantId = link.dataset.aroxSelect;
  const input = variantId ? variantInputs().find((el) => el.value === variantId) : null;
  if (input && !input.disabled) {
    input.checked = true;
    syncSelection(input, true);
  }

  scrollToTarget(target);
  // Like a native in-page link, move keyboard focus to the destination: for the order panel,
  // that is the selected offer, so Tab continues through the form rather than the header; for
  // the offers, the section heading, so the next Tab reaches the first card's button.
  let focusTarget = input ?? (isOrderTarget(target) ? variantInputs().find((el) => el.checked && !el.disabled) : null);
  if (!focusTarget && isOffersTarget(target)) {
    focusTarget = target.closest('section')?.querySelector('h2') ?? null;
    focusTarget?.setAttribute('tabindex', '-1');
  }
  if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
}

/**
 * Leaves exactly one unit of the selected AROX offer in the cart.
 * Only lines of the two AROX variants are touched: the other offer is removed (so the
 * buyer never checks out both by mistake) and the selected one is set to quantity 1.
 * Unrelated cart items are kept as they are.
 * @param {string} selectedId
 * @param {string[]} offerIds - Variant IDs of both AROX offers
 */
async function prepareCart(selectedId, offerIds) {
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
  if (!cartResponse.ok) throw new Error(`cart ${cartResponse.status}`);
  const cart = await cartResponse.json();

  /** @type {Record<string, number>} */
  const updates = {};
  let hasSelected = false;
  for (const item of cart.items ?? []) {
    const variantId = String(item.variant_id);
    if (variantId === selectedId) {
      if (hasSelected) updates[item.key] = 0;
      else if (item.quantity !== 1) updates[item.key] = 1;
      hasSelected = true;
    } else if (offerIds.includes(variantId)) {
      updates[item.key] = 0;
    }
  }

  if (Object.keys(updates).length > 0) {
    const response = await fetch(`${root}cart/update.js`, { method: 'POST', headers: json, body: JSON.stringify({ updates }) });
    if (!response.ok) throw new Error(`cart/update ${response.status}`);
  }
  if (!hasSelected) {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: json,
      body: JSON.stringify({ items: [{ id: Number(selectedId), quantity: 1 }] }),
    });
    if (!response.ok) throw new Error(`cart/add ${response.status}`);
  }
}

/** @param {HTMLFormElement} form */
function enhanceOrderForm(form) {
  const button = form.querySelector('[data-arox-submit]');
  const label = form.querySelector('[data-arox-submit-label]');
  const error = form.querySelector('[data-arox-error]');
  const idleLabel = label?.textContent ?? '';

  form.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-arox-variant]')) {
      syncSelection(event.target, true);
    }
  });

  form.addEventListener('submit', async (event) => {
    if (form.hasAttribute('data-arox-direct-active')) return; // the one-page COD flow handles it
    event.preventDefault();
    const checked = form.querySelector('input[data-arox-variant]:checked');
    if (!(checked instanceof HTMLInputElement) || !(button instanceof HTMLButtonElement)) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = button.dataset.loadingLabel ?? idleLabel;
    if (error instanceof HTMLElement) error.hidden = true;

    const reset = () => {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (label) label.textContent = idleLabel;
    };

    try {
      const offerIds = (form.dataset.aroxOfferIds ?? '').split(',').filter(Boolean);
      await prepareCart(checked.value, offerIds);
      window.location.assign('/checkout');
    } catch (err) {
      if (err instanceof TypeError) {
        // Network-level failure: let the browser submit the native form instead.
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
      reset();
      if (error instanceof HTMLElement) error.hidden = false;
    }
  });
}

// ---------- One-page Cash on Delivery order ----------

const NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M}' .’-]*$/u;
// The buyer's unsent details (this tab only), so a reload, a language switch or an error never loses them.
const DIRECT_DRAFT_KEY = 'arox-direct-draft';
const DIRECT_EVENTS_KEY = 'arox-direct-events';

/** Same rules as the order app (arox-order-service/src/validate.js): +216 and 8 digits, 2–9 first. */
function normalizeTunisianPhone(raw) {
  let digits = raw.replace(/[٠-٩۰-۹]/g, (d) => String(d.charCodeAt(0) & 0xf)).replace(/[\s().\-/ ]/g, '');
  if (digits.startsWith('+')) digits = digits.slice(1);
  else if (digits.startsWith('00')) digits = digits.slice(2);
  if (!/^\d+$/.test(digits)) return null;
  if (digits.length === 11 && digits.startsWith('216')) digits = digits.slice(3);
  else if (digits.length === 9 && digits.startsWith('0')) digits = digits.slice(1);
  return /^[2-9]\d{7}$/.test(digits) ? `+216${digits}` : null;
}

/** Same list as the order app (isPlaceholderPhone): the old example, identical digits, full runs. */
function isPlaceholderPhone(e164) {
  const local = e164.slice(-8);
  if (local === '20123456' || /^(\d)\1{7}$/.test(local)) return true;
  const steps = new Set([...local].slice(1).map((d, i) => Number(d) - Number(local[i])));
  return steps.size === 1 && (steps.has(1) || steps.has(-1));
}

const newIdempotencyKey = () =>
  crypto.randomUUID?.() ?? [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, '0')).join('');

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** sessionStorage can be unavailable (private mode, blocked storage): every access is optional. */
const session = {
  get(key) {
    try {
      return JSON.parse(window.sessionStorage.getItem(key) ?? 'null');
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* optional */
    }
  },
  remove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* optional */
    }
  },
};

/**
 * Turns the order form into the one-page COD form: four required fields (first name, last name,
 * phone, full address) sent to the AROX order app. Errors stay on the page; nothing redirects to
 * checkout.
 * @param {HTMLFormElement} form
 */
function initDirectOrder(form) {
  const endpoint = form.dataset.aroxDirect;
  const fields = form.querySelector('[data-arox-direct-fields]');
  const i18nNode = form.querySelector('[data-arox-direct-i18n]');
  const button = form.querySelector('[data-arox-submit]');
  const label = form.querySelector('[data-arox-submit-label]');
  const error = form.querySelector('[data-arox-error]');
  const status = form.querySelector('[data-arox-direct-status]');
  const success = form.closest('.arox-order__panel')?.querySelector('[data-arox-direct-success]');
  if (!endpoint || !(fields instanceof HTMLFieldSetElement) || !(button instanceof HTMLButtonElement)) return;

  let i18n;
  try {
    i18n = JSON.parse(i18nNode?.textContent ?? '');
  } catch {
    return; // keep the native form
  }

  /** @param {string} name */
  const field = (name) => form.querySelector(`[data-arox-field="${name}"]`);
  const lang = document.documentElement.lang?.startsWith('ar') ? 'ar' : 'fr';
  const checkedOffer = () => form.querySelector('input[data-arox-variant]:checked');

  // Unsent details survive a reload, a language switch or an error (this tab only).
  const DRAFT_TEXT = { f: 'firstName', l: 'lastName', p: 'phone', a: 'fullAddress' };
  const saveDraft = () => {
    const draft = {};
    for (const [short, name] of Object.entries(DRAFT_TEXT)) {
      const el = field(name);
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) draft[short] = el.value;
    }
    session.set(DIRECT_DRAFT_KEY, draft);
  };
  const saved = session.get(DIRECT_DRAFT_KEY);
  for (const [short, name] of Object.entries(DRAFT_TEXT)) {
    const el = field(name);
    if ((el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) && typeof saved?.[short] === 'string' && !el.value) el.value = saved[short];
  }
  for (const name of Object.values(DRAFT_TEXT)) field(name)?.addEventListener('input', saveDraft);

  form.setAttribute('data-arox-direct-active', '');
  const readyAt = performance.now();

  /** The call to action names the selected price ("Confirmer ma commande — 39 DT"). */
  const idleLabel = () => {
    const template = label?.getAttribute('data-arox-price-label');
    const offer = checkedOffer();
    return template && offer instanceof HTMLInputElement ? template.replace('%PRICE%', offer.dataset.price ?? '') : label?.textContent ?? '';
  };

  // Optional Cloudflare Turnstile (Managed, interaction-only). Tokens are single-use: reset after each try.
  // Its script and challenge (several hundred KB) load when the form nears the screen or is touched,
  // not with the page; a submission still waits for a token, so protection is unchanged.
  const turnstileBox = form.querySelector('[data-arox-turnstile]');
  let turnstileId = null;
  let turnstileToken = '';
  /** @type {Promise<void> | null} */
  let turnstileLoad = null;
  const renderTurnstile = () => {
    if (!(turnstileBox instanceof HTMLElement) || turnstileId !== null || !window.turnstile) return;
    turnstileId = window.turnstile.render(turnstileBox, {
      sitekey: turnstileBox.dataset.sitekey,
      action: 'arox_order',
      appearance: 'interaction-only',
      theme: 'dark',
      language: lang,
      callback: (token) => (turnstileToken = token),
      'expired-callback': () => (turnstileToken = ''),
    });
  };
  const loadTurnstile = () => {
    if (!(turnstileBox instanceof HTMLElement)) return Promise.resolve();
    turnstileLoad ??= new Promise((resolve) => {
      if (window.turnstile || !turnstileBox.dataset.src) return resolve();
      const script = document.createElement('script');
      script.src = turnstileBox.dataset.src;
      script.async = true;
      // A blocked script resolves too: the order app then answers verification_failed, as before.
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => resolve(), { once: true });
      document.head.append(script);
    }).then(renderTurnstile);
    return turnstileLoad;
  };
  if (turnstileBox) {
    const near = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        near.disconnect();
        loadTurnstile();
      },
      { rootMargin: '600px 0px' }
    );
    near.observe(form);
    form.addEventListener('focusin', loadTurnstile, { once: true });
    form.addEventListener('pointerdown', loadTurnstile, { once: true, passive: true });
  }
  const resetTurnstile = () => {
    turnstileToken = '';
    if (turnstileId !== null) window.turnstile?.reset(turnstileId);
  };
  /** A fast buyer can submit before Turnstile has issued its token: wait for it (bounded) rather than fail. */
  const turnstileReady = async (ms = 8000) => {
    if (!turnstileBox) return;
    await loadTurnstile();
    for (const end = Date.now() + ms; !turnstileToken && Date.now() < end; ) await sleep(200);
  };

  let started = false;
  form.addEventListener('focusin', (event) => {
    if (started || !(event.target instanceof Element) || !fields.contains(event.target)) return;
    started = true;
    const offer = checkedOffer();
    window.Shopify?.analytics?.publish?.('arox_order_started', {
      variantId: offer instanceof HTMLInputElement ? offer.value : '',
      value: offer instanceof HTMLInputElement ? Number(offer.dataset.priceCents) / 100 : 0,
      currency: 'TND',
    });
  });

  // --- Validation (same rules as the order app; the app re-checks everything) ----------------
  const FIELD_ORDER = ['firstName', 'lastName', 'phone', 'fullAddress'];
  const errorFor = (name) => form.querySelector(`[data-arox-field-error="${name}"]`);

  function setFieldError(name, message) {
    const input = field(name);
    const slot = errorFor(name);
    if (input instanceof HTMLElement) {
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
    if (slot instanceof HTMLElement) {
      slot.textContent = message ?? '';
      slot.hidden = !message;
    }
  }

  const value = (name) => {
    const input = field(name);
    return input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement ? input.value.replace(/\s+/g, ' ').trim() : '';
  };

  /** @returns {Record<string, string>} message per invalid field */
  function errorsOf() {
    const errors = {};
    for (const name of ['firstName', 'lastName']) {
      const v = value(name);
      if (!v) errors[name] = i18n.required;
      else if (v.length > 60 || !NAME_RE.test(v)) errors[name] = i18n.name;
    }
    const phone = normalizeTunisianPhone(value('phone'));
    if (!value('phone')) errors.phone = i18n.required;
    else if (!phone) errors.phone = i18n.phone;
    else if (isPlaceholderPhone(phone)) errors.phone = i18n.phone_example ?? i18n.phone;
    // Lenient, like the app: at least 8 characters and two letters (any script).
    const address = value('fullAddress');
    if (!address) errors.fullAddress = i18n.required;
    else if (address.length < 8 || address.length > 250 || !/\p{L}.*\p{L}/su.test(address)) errors.fullAddress = i18n.address;
    return errors;
  }

  // Progressive feedback: a filled field is checked when the buyer leaves it; typing clears its error.
  // Not when the buyer leaves it for the submit button: the new error line would push the button
  // away before the tap lands, and the submit checks every field anyway.
  let pressingSubmit = false;
  button.addEventListener('pointerdown', () => {
    pressingSubmit = true;
    setTimeout(() => (pressingSubmit = false), 700);
  });
  for (const name of FIELD_ORDER) {
    const input = field(name);
    input?.addEventListener('input', () => setFieldError(name, null));
    input?.addEventListener('blur', () => {
      if (!pressingSubmit && value(name)) setFieldError(name, errorsOf()[name] ?? null);
    });
  }

  // A valid number is shown in its national form ("98 765 432"): +216 is already displayed beside it.
  const phoneInput = field('phone');
  phoneInput?.addEventListener('blur', () => {
    if (!(phoneInput instanceof HTMLInputElement)) return;
    const normalized = normalizeTunisianPhone(phoneInput.value);
    if (normalized) phoneInput.value = normalized.slice(4).replace(/^(\d{2})(\d{3})(\d{3})$/, '$1 $2 $3');
  });

  function payload() {
    const offer = checkedOffer();
    return {
      variantId: offer instanceof HTMLInputElement ? offer.value : '',
      firstName: value('firstName'),
      lastName: value('lastName'),
      phone: normalizeTunisianPhone(value('phone')) ?? value('phone'),
      fullAddress: value('fullAddress'),
      locale: lang,
      website: value('website'),
      pageUrl: `${window.location.origin}${window.location.pathname}`,
    };
  }

  function showErrors(errors) {
    for (const name of FIELD_ORDER) setFieldError(name, errors[name] ?? null);
    const first = FIELD_ORDER.map((name) => (errors[name] ? field(name) : null)).find((el) => el instanceof HTMLElement);
    if (first instanceof HTMLElement) {
      // Centre the field so the mobile keyboard cannot hide it or its message.
      first.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      first.focus({ preventScroll: true });
    }
  }

  // --- Submission ----------------------------------------------------------------------------
  let sending = false;
  let key = null; // one key per order intent; kept across retries until the app gives a definitive answer

  const setBusy = (busy) => {
    sending = busy;
    button.disabled = busy;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    if (label) label.textContent = busy ? button.dataset.directLoadingLabel ?? idleLabel() : idleLabel();
    if (status instanceof HTMLElement) status.textContent = busy ? button.dataset.directLoadingLabel ?? '' : '';
  };

  const fail = (message) => {
    setBusy(false);
    resetTurnstile(); // its token is single-use: the next attempt needs a fresh one
    if (error instanceof HTMLElement && message) {
      error.textContent = message;
      error.hidden = false;
    }
  };

  success?.querySelector('[data-arox-print]')?.addEventListener('click', () => window.print());

  function succeed(result) {
    setBusy(false);
    key = null;
    session.remove(DIRECT_DRAFT_KEY);
    const offer = checkedOffer();
    const cents = Math.round(Number(result.order.total) * 100);
    const total =
      offer instanceof HTMLInputElement && cents === Number(offer.dataset.priceCents)
        ? offer.dataset.price ?? ''
        : `${Number(result.order.total).toLocaleString(lang === 'ar' ? 'ar-TN' : 'fr-TN')} ${result.order.currency === 'TND' ? (lang === 'ar' ? 'دينار' : 'DT') : result.order.currency}`;

    // Purchase signal for ad pixels, once per created order (event_id shared with the server event).
    const seen = session.get(DIRECT_EVENTS_KEY) ?? [];
    if (result.eventId && !seen.includes(result.eventId)) {
      session.set(DIRECT_EVENTS_KEY, [...seen, result.eventId].slice(-10));
      window.Shopify?.analytics?.publish?.('arox_order_placed', {
        event_id: result.eventId,
        order_id: result.order.id,
        order_name: result.order.name,
        variant_id: result.order.variantId,
        value: Number(result.order.total),
        currency: result.order.currency,
      });
    }

    if (success instanceof HTMLElement) {
      const set = (selector, text) => {
        const el = success.querySelector(selector);
        if (el) el.textContent = text;
      };
      set('[data-arox-success-ref]', result.order.name ?? '');
      const refRow = success.querySelector('[data-arox-success-ref-row]');
      if (refRow instanceof HTMLElement) refRow.hidden = !result.order.name;
      set('[data-arox-success-offer]', offer instanceof HTMLInputElement ? offer.dataset.name ?? '' : result.order.offer ?? '');
      set('[data-arox-success-total]', total);
      // Receipt rows repeat what the buyer typed, so the printed summary covers the whole sale.
      const phone = normalizeTunisianPhone(value('phone'));
      set('[data-arox-success-date]', new Date().toLocaleString(lang === 'ar' ? 'ar-TN' : 'fr-TN', { dateStyle: 'long', timeStyle: 'short' }));
      set('[data-arox-success-customer]', `${value('firstName')} ${value('lastName')}`.trim());
      set('[data-arox-success-phone]', phone ? `+216 ${phone.slice(4).replace(/^(\d{2})(\d{3})(\d{3})$/, '$1 $2 $3')}` : value('phone'));
      set('[data-arox-success-address]', value('fullAddress'));
      document.documentElement.classList.add('arox-print-receipt');
      form.hidden = true;
      success.hidden = false;
      success.focus({ preventScroll: true });
      const box = success.getBoundingClientRect();
      revealBlock(box.top, box.bottom, scrollBehavior());
    }
  }

  async function send(body) {
    setBusy(true);
    if (error instanceof HTMLElement) error.hidden = true;
    key ??= newIdempotencyKey();
    for (let attempt = 0; ; attempt++) {
      await turnstileReady();
      let response;
      let json = null;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20_000);
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ ...body, idempotencyKey: key, elapsedMs: Math.round(performance.now() - readyAt), turnstileToken: turnstileToken || undefined }),
          signal: controller.signal,
        });
        json = await response.json().catch(() => null);
      } catch {
        // Offline, timeout or blocked: the order may or may not exist, so the key is kept and a
        // retry can only return the same order.
        clearTimeout(timer);
        fail(i18n.network);
        return;
      }
      clearTimeout(timer);

      if (response.ok && json?.ok === true && json.order?.name) return succeed(json);
      const code = json?.code;
      // The service's correlation reference, so a failure can be traced without any customer data.
      const ref = typeof json?.ref === 'string' && /^[a-z0-9]{6,16}$/.test(json.ref) ? json.ref : '';
      const withRef = (message) => (ref && i18n.reference ? `${message} ${i18n.reference.replace('%REF%', ref)}` : message);
      if (code === 'verification_failed') return fail(withRef(i18n.verification));
      if (code === 'in_progress' || code === 'order_unconfirmed') {
        if (attempt < 4) {
          await sleep(2500);
          resetTurnstile(); // a fresh token is needed for each request; the next pass waits for it
          continue;
        }
        return fail(withRef(i18n.order_unconfirmed));
      }
      if (code === 'test_mode') {
        key = null; // nothing was created
        return fail(withRef(i18n.test_mode ?? i18n.order_failed));
      }
      if (response.status === 401 || code === 'forbidden') return fail(withRef(i18n.session ?? i18n.order_failed));
      if (response.status === 422 && json?.fields) {
        key = null; // nothing was created
        const messages = {};
        for (const [name, reason] of Object.entries(json.fields)) {
          if (name === 'phone') messages[name] = reason === 'required' ? i18n.required : reason === 'example' ? i18n.phone_example ?? i18n.phone : i18n.phone;
          else if (name === 'fullAddress') messages[name] = reason === 'required' ? i18n.required : i18n.address;
          else if (name === 'firstName' || name === 'lastName') messages[name] = reason === 'required' ? i18n.required : i18n.name;
        }
        fail(i18n.summary);
        return showErrors(messages);
      }
      if (response.status === 429) return fail(withRef(i18n.rate_limited));
      if (code === 'unavailable') {
        key = null;
        return fail(withRef(i18n.unavailable));
      }
      // Not reachable or refused (no app at the endpoint, 5xx, unexpected answer): never a success.
      if (code === 'order_failed') key = null;
      return fail(withRef(i18n.order_failed));
    }
  }

  form.addEventListener('submit', (event) => {
    if (!form.hasAttribute('data-arox-direct-active')) return;
    event.preventDefault();
    if (sending) return; // double clicks and repeated Enter presses
    const errors = errorsOf();
    if (Object.keys(errors).length) {
      if (error instanceof HTMLElement) {
        error.textContent = i18n.summary;
        error.hidden = false;
      }
      showErrors(errors);
      return;
    }
    for (const name of FIELD_ORDER) setFieldError(name, null);
    send(payload());
  });
}

/**
 * Homepage mobile menu (native details): closes after a link is chosen, on Escape
 * (returning focus to its toggle) and on a click outside it.
 */
function initMenus() {
  const menus = [...document.querySelectorAll('details[data-arox-menu]')].filter((el) => el instanceof HTMLDetailsElement);
  if (menus.length === 0) return;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    for (const menu of menus) {
      if (!menu.open) continue;
      if (!target || !menu.contains(target) || target.closest('a')) menu.open = false;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    for (const menu of menus) {
      if (!menu.open) continue;
      menu.open = false;
      menu.querySelector('summary')?.focus();
    }
  });
}

/**
 * FAQ search: filters every help item on the page by its question and answer text, hides
 * topics left empty, and reports the count through the search box's own live region.
 */
function initHelpSearch() {
  const input = document.querySelector('input[data-arox-help-input]');
  if (!(input instanceof HTMLInputElement)) return;
  const status = document.querySelector('[data-arox-help-status]');
  const empty = document.querySelector('[data-arox-help-empty]');
  const items = [...document.querySelectorAll('[data-arox-help-item]')].filter((el) => el instanceof HTMLDetailsElement);
  const groups = [...document.querySelectorAll('[data-arox-help-group]')];
  const normalize = (text) => text.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const index = items.map((item) => ({ item, text: normalize(item.textContent ?? '') }));
  let timer = 0;

  const apply = () => {
    const query = normalize(input.value.trim());
    let shown = 0;
    for (const { item, text } of index) {
      const match = query === '' || text.includes(query);
      item.hidden = !match;
      if (match) shown += 1;
      if (query !== '' && match) item.open = true;
    }
    for (const group of groups) {
      const visible = group.querySelector('[data-arox-help-item]:not([hidden])') || group.querySelector('[data-arox-help-search]');
      group.classList.toggle('is-empty', !visible);
    }
    if (empty instanceof HTMLElement) empty.hidden = shown > 0;
    if (status instanceof HTMLElement) {
      status.textContent = query === '' ? '' : (input.dataset.resultsLabel ?? '').replace('__COUNT__', String(shown));
    }
  };

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(apply, 150);
  });
}

/** Legal pages: builds the table of contents from the document's H2 headings. */
function initLegalToc() {
  const doc = document.querySelector('[data-arox-legal-doc]');
  const nav = document.querySelector('[data-arox-toc]');
  const list = document.querySelector('[data-arox-toc-list]');
  if (!doc || !(nav instanceof HTMLElement) || !list) return;
  const headings = [...doc.querySelectorAll('h2')];
  if (headings.length < 2) return;
  headings.forEach((heading, i) => {
    if (!heading.id) heading.id = `arox-legal-${i + 1}`;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#${heading.id}`;
    a.textContent = heading.textContent ?? '';
    a.dataset.aroxScroll = '';
    li.append(a);
    list.append(li);
  });
  nav.hidden = false;
}

/** Moves focus to a form's success or error notice after a full-page form post. */
function focusFormNotice() {
  const notice = document.querySelector('[data-arox-focus-on-load]');
  if (notice instanceof HTMLElement) notice.focus();
}

function initStickyBar() {
  const bar = document.querySelector('[data-arox-sticky]');
  if (!(bar instanceof HTMLElement)) return;
  bar.hidden = false;

  const watched = [
    document.querySelector('[data-arox-primary-cta]'),
    document.getElementById('arox-order'),
    document.getElementById('arox-final'),
    document.querySelector('.arox-offers__grid'),
    document.querySelector('[data-arox-plan-picker]'),
  ].filter(Boolean);
  const onScreen = new Set();
  let passedHero = false;

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) onScreen.add(entry.target);
      else onScreen.delete(entry.target);
      if (entry.target.matches('[data-arox-primary-cta]') && !entry.isIntersecting) {
        passedHero = entry.boundingClientRect.top < 0;
      }
    }
    bar.classList.toggle('is-visible', passedHero && onScreen.size === 0);
  });
  for (const el of watched) observer.observe(el);
}

class AroxCarousel extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const track = this.querySelector('.arox-carousel__track');
    if (!(track instanceof HTMLElement)) return;
    const { signal } = this.abort;
    for (const button of this.querySelectorAll('button[data-dir]')) {
      button.addEventListener('click', () => this.page(track, Number(button.getAttribute('data-dir'))), { signal });
    }

    // Remember the first slide in view, and keep it aligned when the track is resized (window
    // resize, phone rotation, scrollbar change): slide widths change, scrollLeft does not.
    // Geometry is first read in the observer's initial callback, after layout, so connecting the
    // element never forces a synchronous layout of the page.
    this.index = 0;
    this.stride = 0;
    track.addEventListener(
      'scroll',
      () => {
        const { stride } = this.measure(track);
        if (stride && stride === this.stride) this.index = Math.round(Math.abs(track.scrollLeft) / stride);
      },
      { passive: true, signal }
    );
    this.resizeObserver = new ResizeObserver(() => {
      const { stride, last } = this.measure(track);
      if (!stride || stride === this.stride) return;
      if (!this.stride) {
        this.stride = stride;
        return;
      }
      this.stride = stride;
      this.index = Math.min(this.index, last);
      track.scrollTo({ left: (isRtl() ? -1 : 1) * this.index * stride, behavior: 'instant' });
    });
    this.resizeObserver.observe(track);
  }

  /**
   * Slide geometry: distance between slide starts, whole slides in view, last valid start index.
   * @param {HTMLElement} track
   */
  measure(track) {
    const slides = [...track.children].filter((el) => el instanceof HTMLElement);
    if (slides.length < 2) return { stride: 0, perView: 1, last: 0 };
    const stride = Math.abs(slides[1].offsetLeft - slides[0].offsetLeft);
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const perView = stride ? Math.max(1, Math.round((track.clientWidth + gap) / stride)) : 1;
    return { stride, perView, last: Math.max(slides.length - perView, 0) };
  }

  /**
   * Moves one page (the number of whole slides in view) and lands exactly on a slide edge, so
   * repeated presses never drift and the last page is full. scrollLeft is negative in RTL.
   * @param {HTMLElement} track
   * @param {number} dir - 1 for next, -1 for previous
   */
  page(track, dir) {
    const { stride, perView, last } = this.measure(track);
    if (!stride) return;
    const current = Math.round(Math.abs(track.scrollLeft) / stride);
    const target = Math.min(Math.max(current + dir * perView, 0), last);
    track.scrollTo({ left: (isRtl() ? -1 : 1) * target * stride, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
  }
}

class AroxReviews extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const button = this.querySelector('.arox-reviews__more');
    if (!(button instanceof HTMLButtonElement)) return;
    button.addEventListener(
      'click',
      () => {
        const expanded = this.classList.toggle('is-expanded');
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = (expanded ? button.dataset.labelLess : button.dataset.labelMore) ?? '';
      },
      { signal: this.abort.signal }
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
  }
}

if (!customElements.get('arox-carousel')) customElements.define('arox-carousel', AroxCarousel);
if (!customElements.get('arox-reviews')) customElements.define('arox-reviews', AroxReviews);

document.addEventListener('click', handleClick);
// The phone plan picker is a GET form to the product page. When this page already has the order
// form (the product page itself), select the plan there and scroll to it instead of reloading.
document.addEventListener('submit', (event) => {
  const picker = event.target;
  if (!(picker instanceof HTMLFormElement) || !picker.matches('[data-arox-plan-picker]')) return;
  const chosen = picker.querySelector('input[name="variant"]:checked');
  const input = chosen instanceof HTMLInputElement ? variantInputs().find((el) => el.value === chosen.value && !el.disabled) : null;
  if (!input) return;
  event.preventDefault();
  input.checked = true;
  syncSelection(input, true);
  scrollToOrder();
  input.focus({ preventScroll: true });
});
for (const form of document.querySelectorAll('form[data-arox-order-form]')) {
  if (!(form instanceof HTMLFormElement)) continue;
  enhanceOrderForm(form);
  if (form.dataset.aroxDirect) initDirectOrder(form);
}
const initial = variantInputs().find((el) => el.checked);
if (initial) syncSelection(initial);
// Arriving from another page on an order or offers hash (e.g. a homepage offer link, or the
// header's "Abonnements" link from a content page): the browser's native (smooth) jump lands on
// the anchor; jump straight to the purchase view once layout has settled.
const arrival = { '#arox-buy': scrollToOrder, '#arox-order': scrollToOrder, '#arox-plans': scrollToOffers, '#arox-offers': scrollToOffers }[window.location.hash];
if (arrival) {
  const settle = () => requestAnimationFrame(() => arrival('instant'));
  if (document.readyState === 'complete') settle();
  else window.addEventListener('load', settle, { once: true });
}
// The header height feeds the CSS scroll margin; observed (read after layout) rather than measured
// while the page is still building, and kept current when the header wraps or the font loads.
const siteHeader = document.querySelector('.arox-section-header');
if (siteHeader) new ResizeObserver(headerOffset).observe(siteHeader);
initMenus();
initHelpSearch();
initLegalToc();
focusFormNotice();
initStickyBar();
